import { resolveThumbnailUrl } from "./imageVariants";
import { normalizeBookingSearchDates } from "./bookingSearchDates";

const STORAGE_KEY = "ecoTourContinueBooking";
const MAX_ITEMS = 5;
const UPDATED_EVENT = "ecotour:continue-booking-updated";

const notifyContinueBookingChanged = (detail = {}) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(UPDATED_EVENT, { detail }));
};

const resolveImageValue = (value) => {
  return resolveThumbnailUrl(value, "mid", true) || "";
};

const normalizeContinueMode = (value = "", fallback = "") => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "stay" || raw === "stays" || raw === "room" || raw === "nightstay" || raw === "night_stay") {
    return "stays";
  }
  if (raw === "experience" || raw === "experiences") return "experiences";
  return fallback ? normalizeContinueMode(fallback) : "stays";
};

const normalizeSearchState = (searchState, fallbackType = "") => {
  if (!searchState || typeof searchState !== "object") return null;
  const mode = normalizeContinueMode(searchState.mode, fallbackType);
  const normalizedDates = normalizeBookingSearchDates({
    mode,
    startDate: searchState.startDate || searchState.date,
    endDate: searchState.endDate,
  });
  return {
    ...searchState,
    mode,
    startDate: normalizedDates.startDate,
    endDate: normalizedDates.endDate,
    date: normalizedDates.startDate,
  };
};

const normalizeEntry = (entry = {}) => {
  const key = String(entry.key || "").trim();
  if (!key) return null;
  const normalizedType = String(entry.type || "").trim() || "stay";
  return {
    key,
    type: normalizedType,
    identifier: String(entry.identifier || "").trim(),
    title: String(entry.title || "").trim() || "EcoTour Odisha",
    subtitle: String(entry.subtitle || "").trim(),
    image: resolveImageValue(entry.image),
    href: String(entry.href || "").trim() || "/stays-camps",
    searchState: normalizeSearchState(entry.searchState, normalizedType),
    resumeConfig:
      entry.resumeConfig && typeof entry.resumeConfig === "object" && !Array.isArray(entry.resumeConfig)
        ? {
            quantity: Math.max(1, Number(entry.resumeConfig.quantity || 1) || 1),
            ratePlanId: String(entry.resumeConfig.ratePlanId || "").trim(),
            ratePlanLabel: String(entry.resumeConfig.ratePlanLabel || "").trim(),
            addOns: Array.isArray(entry.resumeConfig.addOns) ? entry.resumeConfig.addOns : [],
            selectedCharges: Array.isArray(entry.resumeConfig.selectedCharges)
              ? entry.resumeConfig.selectedCharges
              : [],
            requiredCharges: Array.isArray(entry.resumeConfig.requiredCharges)
              ? entry.resumeConfig.requiredCharges
              : [],
          }
        : null,
    updatedAt: Number(entry.updatedAt) || Number(entry.timestamp) || Date.now(),
  };
};

export const readContinueBooking = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => normalizeEntry(entry))
      .filter(Boolean)
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
};

export const recordContinueBooking = (entry) => {
  if (typeof window === "undefined") return;
  const normalized = normalizeEntry(entry);
  if (!normalized) return;
  const existing = readContinueBooking();
  const filtered = existing.filter((item) => item.key !== normalized.key);
  const next = [{ ...normalized, updatedAt: Date.now() }, ...filtered].slice(0, MAX_ITEMS);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    notifyContinueBookingChanged({ action: "record", key: normalized.key });
  } catch {
    // ignore persistence errors
  }
};

export const clearContinueBooking = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    notifyContinueBookingChanged({ action: "clear" });
  } catch {
    // ignore
  }
};

export const removeContinueBookingByKey = (key) => {
  if (typeof window === "undefined") return;
  const normalizedKey = String(key || "").trim();
  if (!normalizedKey) return;
  const existing = readContinueBooking();
  const next = existing.filter((item) => item.key !== normalizedKey);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    notifyContinueBookingChanged({ action: "remove", key: normalizedKey });
  } catch {
    // ignore persistence errors
  }
};

const normalizeDateValue = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const fromIso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (fromIso?.[1]) return fromIso[1];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const toContinueMode = (type = "") => {
  return normalizeContinueMode(type, "experiences");
};

const normalizeIdentifierValue = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  const parts = raw.split(":").map((part) => part.trim()).filter(Boolean);
  if (!parts.length) return "";
  // Keep canonical resource part to tolerate prefixed type variants, e.g.
  // "jungle-safari:foo:bar" vs "foo:bar".
  return parts.length > 3 ? parts.slice(1).join(":") : parts.join(":");
};

const identifiersRepresentSameInventory = (left, right) => {
  const a = normalizeIdentifierValue(left);
  const b = normalizeIdentifierValue(right);
  if (!a || !b) return false;
  return a === b;
};

const matchesPurchasedInventory = (entry = {}, purchase = {}) => {
  const search = entry.searchState || {};
  const entryMode = String(search.mode || "").toLowerCase();
  const purchaseMode = toContinueMode(purchase.type);
  if (entryMode && purchaseMode && entryMode !== purchaseMode) return false;

  const entryIdentifier = String(entry.identifier || "").trim();
  const purchaseIdentifier = String(purchase.identifier || "").trim();
  const identifierMatched = identifiersRepresentSameInventory(entryIdentifier, purchaseIdentifier);

  if (identifierMatched) {
    if (purchaseMode === "stays") {
      const entryStart = normalizeDateValue(search.startDate);
      const entryEnd = normalizeDateValue(search.endDate);
      const purchaseStart = normalizeDateValue(purchase.checkIn || purchase.travelDate);
      const purchaseEnd = normalizeDateValue(purchase.checkOut);
      if (purchaseStart && entryStart && purchaseStart !== entryStart) return false;
      if (purchaseEnd && entryEnd && purchaseEnd !== entryEnd) return false;
    } else {
      const entryDate = normalizeDateValue(search.startDate || search.date);
      const purchaseDate = normalizeDateValue(purchase.travelDate || purchase.checkIn);
      if (purchaseDate && entryDate && purchaseDate !== entryDate) return false;
    }
    return true;
  }

  // If both identifiers are present but don't match, do not remove.
  if (entryIdentifier && purchaseIdentifier) return false;

  const entryStart = normalizeDateValue(search.startDate || search.date);
  const entryEnd = normalizeDateValue(search.endDate);
  const purchaseStart = normalizeDateValue(purchase.checkIn || purchase.travelDate);
  const purchaseEnd = normalizeDateValue(purchase.checkOut);
  if (!purchaseStart || !entryStart || purchaseStart !== entryStart) return false;
  if (purchaseMode === "stays" && purchaseEnd && entryEnd && purchaseEnd !== entryEnd) return false;
  return true;
};

export const removePurchasedContinueBookings = (purchases = []) => {
  if (typeof window === "undefined") return;
  const normalizedPurchases = (Array.isArray(purchases) ? purchases : [purchases]).filter(Boolean);
  if (!normalizedPurchases.length) return;
  const existing = readContinueBooking();
  if (!existing.length) return;
  const next = existing.filter(
    (entry) => !normalizedPurchases.some((purchase) => matchesPurchasedInventory(entry, purchase)),
  );
  if (next.length === existing.length) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    notifyContinueBookingChanged({ action: "purchase-cleanup" });
    window.dispatchEvent(new CustomEvent("ecotour:continue-booking", { detail: { cleanup: true } }));
  } catch {
    // ignore persistence errors
  }
};
